import React from 'react';
import Fader from 'react-fader';
import { Route, Redirect } from 'react-router-dom';
import Switch from 'react-router-transition-switch';

import ApplicationLayout from 'components/ApplicationLayout';
import useIsMobile from 'hooks/useIsMobile';
import Booking from 'screens/components/Booking';
import CustomerSupportV2 from 'screens/components/CustomerSupportV2';
import GuestNotAuthorized from 'screens/components/Login/forms/GuestNotAuthorized';
import Root from 'screens/components/Root';
import TravelRequested from 'screens/components/TravelRequested';
import AutosList from 'screens/containers/AutosList';
import CostCenterReportContainer from 'screens/containers/CostCenterReportContainer';
import Dashboard from 'screens/containers/Dashboard';
import Expense from 'screens/containers/Expense';
import ExpensesList from 'screens/containers/ExpensesList';
import ExpensesListImport from 'screens/containers/ExpensesListImport';
import FlightsList from 'screens/containers/FlightsList';
import HotelsList from 'screens/containers/HotelsList';
import InviteContainer from 'screens/containers/Invite';
import Login from 'screens/containers/Login';
import LoginEmail from 'screens/containers/LoginEmail';
import LoginSso from 'screens/containers/LoginSso';
import ForgotPassword from 'screens/containers/ForgotPassword';
import OAuthDefaultContainer from 'screens/containers/OAuthDefaultContainer';
import RegistrationOnboardingContainer from 'screens/containers/RegistrationOnboarding';
import RequestHistory from 'screens/containers/RequestHistory';
import TravelerProfile from 'screens/containers/TravelerProfile';
import TravelerProfileEdit from 'screens/containers/TravelerProfileEdit';
import TravelerTripPreferences from 'screens/containers/TravelerTripPreferences';
import TravelsWithExpensesList from 'screens/containers/TravelsWithExpensesList';
import UserCountrySelectionContainer from 'screens/containers/UserCountrySelection';
import UsersListContainer from 'screens/containers/Users';
import UsersForm from 'screens/containers/UsersForm';
import { OauthNsLogin } from './publicRouter';
import PaymentAddMethod from 'screens/containers/PaymentAddMethod';
import PaymentCardSelection from 'screens/containers/PaymentCardSelection';
import CreateResellerContainer from 'screens/containers/CreateReseller';

interface Props {
  component: React.ComponentType<any>;
  exact?: boolean;
  path: string;
  isGroupApprover: boolean;
}

const PrivateRoute = ({ component: Component, isGroupApprover, ...props }: Props) => (
  <Route
    {...props}
    render={(props) =>
      isGroupApprover ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{
            pathname: '/corporate/booking',
            state: { from: props.location },
          }}
        />
      )
    }
  />
);

interface TProps {
  isGroupApprover: boolean;
  isTravelArranger: boolean;
}

const TravelerRouter = ({ isGroupApprover, isTravelArranger }: TProps) => {
  const isMobile = useIsMobile();

  return (
    <Switch
      render={({ children }) => (
        <ApplicationLayout>
          <Fader viewStyle={isMobile ? { height: '100vh' } : {}}>{children}</Fader>
        </ApplicationLayout>
      )}
    >
      <Route path="/corporate/invite" component={InviteContainer} />
      <Route path="/corporate/registration" component={RegistrationOnboardingContainer} />
      <Route exact path="/corporate/login-email" component={LoginEmail} />
      <Route exact path="/corporate/login/sso" component={LoginSso} />
      <Route exact path="/corporate/login/forgotpassword" component={ForgotPassword} />
      <Route path="/corporate/login" component={Login} />
      <Route path="/corporate/customerSupport" component={CustomerSupportV2} />
      <Route path="/corporate/travelRequested" component={TravelRequested} />
      <Route path="/corporate/booking" component={Booking} />
      <Route exact path="/corporate/dashboard" component={Dashboard} />
      <Route exact path="/corporate/dashboard/v2" component={Dashboard} />
      <Route path="/corporate/flights" component={FlightsList} />
      <Route path="/corporate/hotels" component={HotelsList} />
      <Route path="/corporate/autos" component={AutosList} />
      <Route exact path="/corporate/paymentMethods" component={PaymentCardSelection} />
      <Route exact path="/corporate/paymentMethods/add" component={PaymentAddMethod} />
      <Route exact path="/corporate/travelsWithExpenses" component={TravelsWithExpensesList} />
      <Route exact path="/corporate/expense" component={Expense} />
      <Route exact path="/corporate/expense/:expenseId" component={Expense} />
      <Route exact path="/corporate/expenses/:travelId" component={ExpensesList} />
      <Route exact path="/corporate/expensesImport/:travelId" component={ExpensesListImport} />
      <Route exact path="/corporate/createReseller" component={CreateResellerContainer} />
      <PrivateRoute
        isGroupApprover={isGroupApprover}
        exact
        path="/corporate/requestHistory"
        component={RequestHistory}
      />
      <Route exact path="/corporate/travelerProfile" component={TravelerProfile} />
      <Route exact path="/corporate/travelerProfileEdit" component={TravelerProfileEdit} />
      <Route exact path="/corporate/travelerProfile/preferences/trip" component={TravelerTripPreferences} />
      <Route exact path="/corporate/oauth-login" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/oauth-registration" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/oauth-invite" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/costCenterReport" component={CostCenterReportContainer} />
      <Route exact path="/corporate/login-not-authorized" component={GuestNotAuthorized} />
      <Route exact path="/corporate/user-country-selection" component={UserCountrySelectionContainer} />

      {isTravelArranger && (
        <>
          <Route exact path="/corporate/usersList" component={UsersListContainer} />
          <Route exact path="/corporate/usersForm" component={UsersForm} />
          <Route exact path="/corporate/usersForm/:userId" component={UsersForm} />
        </>
      )}

      <Route exact path="/corporate" component={Root} />
      <Route exact path="/corporate/" component={Root} />
      <Route exact path="/corporate/auth/:token" component={OauthNsLogin} />
    </Switch>
  );
};

export default TravelerRouter;
